import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";

import { AreaManagementTableRow } from "../components";
import {
  AreaManagementTableHeadCells,
  AreaManagementDeletedHeadCell,
  getComparator,
  stableSort,
  routes,
} from "../../../../constants";
import { useNavigate } from "react-router-dom";
import { useAreasContext } from "../../../../providers/OrganizationAdmin/AreaProvider";
import TableHeadComponent from "../../../../common/TableHeadComponent";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { updateArea } from "../../hooks/AreaHook";

export default function AreaManagementTable({
  selected,
  setSelected,
  areaData,
  setAreaData,
  isLoading = false,
  showDeletedColumn = false,
  allowDrag = true,
}) {
  // Superadmins get an extra "Deleted" column with a restore toggle.
  const headCells = useMemo(
    () =>
      showDeletedColumn
        ? [...AreaManagementTableHeadCells, AreaManagementDeletedHeadCell]
        : AreaManagementTableHeadCells,
    [showDeletedColumn]
  );

  const {
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    rowsPerPage,
    setLoading,
    handleGetAllAreas,
  } = useAreasContext();
  const navigate = useNavigate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - areaData.length)
      : 0;

  // No column sort selected -> keep saved position order.
  const visibleRows = useMemo(
    () =>
      orderBy ? stableSort(areaData, getComparator(order, orderBy)) : areaData,
    [order, orderBy, page, rowsPerPage, areaData]
  );

  // Drag-and-drop reorders positions, so it only makes sense when rows are in
  // position order (unsorted, or sorted by Position ascending).
  const sortedByPositionAsc =
    !orderBy ||
    (orderBy === "position" &&
      visibleRows.every(
        (r, i) => i === 0 || (visibleRows[i - 1]?.position ?? 0) <= (r?.position ?? 0)
      ));
  // Also disabled while a filter/search hides rows: renumbering only the
  // visible rows would give hidden areas duplicate positions.
  const dragEnabled = sortedByPositionAsc && allowDrag;

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      // const newSelected = areaData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };
  // console.log("row", row);

  const handleDragEnd = async (e) => {
    setLoading(true);
    if (!e.destination || !dragEnabled) {
      setLoading(false);
      return;
    }
    try {
      const tempData = Array.from(visibleRows);
      const [source_data] = tempData.splice(e.source.index, 1);
      tempData.splice(e.destination.index, 0, source_data);

      // Show the new order immediately, then save only the changed positions.
      const reordered = tempData.map((item, index) => ({
        ...item,
        position: index + 1,
      }));
      setAreaData(reordered);
      await Promise.all(
        reordered
          .filter((item, index) => tempData[index].position !== item.position)
          .map((item) => updateArea(item.id, { position: item.position }))
      );
      // Reload so the shared area list (and the tabs preview) stay in sync.
      await handleGetAllAreas();
    } catch (error) {
      console.log("error reordering areas", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={mainBoxContainerStyles}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Paper sx={tablePaperContainerStyles}>
          <TableContainer
            style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
          >
            <Table
              stickyHeader
              sx={{ width: "auto" }}
              aria-labelledby="tableTitle"
            >
              {/* <TableContainer style={tableContainerStyles}> */}
              {/* <Table sx={{ width: "auto" }} aria-labelledby="tableTitle"> */}
              <TableHeadComponent
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={visibleRows.length}
                TableHeadData={headCells}
              />
              <Droppable droppableId="droppable-1">
                {(provider) => (
                  <TableBody
                    ref={provider.innerRef}
                    {...provider.droppableProps}
                  >
                    {/* {visibleRows.map((row, index) => { */}
                    {visibleRows.map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const numberType = index % 2 == 0;

                      return (
                        <Draggable
                          key={row?.id}
                          draggableId={row?.id}
                          index={index}
                          isDragDisabled={!dragEnabled}
                        >
                          {(provided) => (
                            <AreaManagementTableRow
                              isItemSelected={isItemSelected}
                              row={row}
                              index={index}
                              handleClickRow={handleClickRow}
                              numberType={numberType}
                              labelId={labelId}
                              areaData={areaData}
                              key={index}
                              handleNavigateToDetails={() => {
                                if (row.name == "All") {
                                } else if (row.name == "Nearby") {
                                } else
                                  navigate(routes.areaDetails, {
                                    state: { areaDetails: row },
                                  });
                              }}
                              provided={provided}
                              showDeletedColumn={showDeletedColumn}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                    {isLoading && visibleRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                            }}
                          >
                            <CircularProgress size={24} sx={{ color: "#00A7CD" }} />
                            Loading areas...
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading && visibleRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No areas found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        </Paper>
      </DragDropContext>
    </Box>
  );
}

const mainBoxContainerStyles = {
  // width: "100%",
  paddingRight: 1,
  border: "none",
  boxShadow: "none",
};

const tablePaperContainerStyles = {
  // maxWidth: "100%",
  mb: 1,
  border: "none",
  boxShadow: "none",
  mt: 1,
};

const scrollbar = { maxWidth: "100%", height: "500px", marginLeft: "auto" };
const scrollbarprops = {
  width: "4px",
  backgroundColor: "#00A7CD",
  borderRadius: "2px",
};
const tableContainerStyles = {
  border: "none",
  boxShadow: "none",
};
